import { rolloutFlags, useRolloutFlagsVersion } from '@chilipiper/rollout'
import {
  getMailboxConnectionStatus,
  getMailboxEmail,
  useConnectMailbox,
  useDisconnectMailbox,
  useMailboxStatus,
} from '@/api/integrations'
import type { MailboxConnectionStatus, MailboxProvider } from '@/api/integrations'
import type { MailboxProviderSlug } from '@/tracking/events'
import { getMailboxReturnUrl } from '@/utils/mailbox-return'

export const mailboxProviderSlug: Record<MailboxProvider, MailboxProviderSlug> = {
  google: 'gmail',
  microsoft: 'outlook_mail',
}

export const isMailboxConnectionEnabled = () => rolloutFlags.mailboxConnection?.isEnabled() ?? false

export type MailboxIntegration = {
  provider: MailboxProvider
  status: MailboxConnectionStatus
  email: string | undefined
  connect: () => Promise<unknown>
  disconnect: () => Promise<unknown>
  /** Drops the stored token and runs consent again, so a scope we added later gets granted. */
  reconnect: () => Promise<unknown>
  isConnecting: boolean
  isDisconnecting: boolean
}

// Pass `undefined` until a primary calendar is connected: the mailbox follows its provider.
export const useMailboxIntegration = (
  provider: MailboxProvider | undefined
): MailboxIntegration | null => {
  // Re-render once the tenant-targeted flags resolve, like LeftNav does.
  useRolloutFlagsVersion()
  const activeProvider = isMailboxConnectionEnabled() ? provider : undefined

  const { data } = useMailboxStatus(activeProvider)
  const { mutateAsync: connect, isPending: isConnecting } = useConnectMailbox(
    activeProvider,
    getMailboxReturnUrl(window.location.href)
  )
  const { mutateAsync: disconnect, isPending: isDisconnecting } =
    useDisconnectMailbox(activeProvider)

  const status = getMailboxConnectionStatus(data)
  if (!activeProvider || !status) return null

  return {
    provider: activeProvider,
    status,
    email: getMailboxEmail(data),
    connect,
    disconnect,
    reconnect: async () => {
      await disconnect()
      await connect()
    },
    isConnecting,
    isDisconnecting,
  }
}
